import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import MiniDrawer from '../components/MiniDrawer';
import AppBarSpacer from '../components/common/AppBarSpacer';
import PageHeader from '../components/common/PageHeader';
import {
  ParentLookupAdminPackage,
  ParentLookupAdminStudent,
  ParentLookupType,
} from '../types/ParentLookup.type';
import {
  lookupParentInformationAdmin,
  updateParentLookupPackage,
} from '../utils/parentLookupApi';
import { notifyAPIClient, notifyFailure } from '../utils/toast';

const ParentLookupAdmin = () => {
  const [open, setOpen] = useState(true);
  const [lookupType, setLookupType] = useState<ParentLookupType>('studentCode');
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState<ParentLookupAdminStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ParentLookupAdminPackage | null>(
    null,
  );
  const [newEndDate, setNewEndDate] = useState('');
  const [remaining, setRemaining] = useState('');
  const [saving, setSaving] = useState(false);

  const lookup = async () => {
    if (!query.trim()) return;
    try {
      setLoading(true);
      const response = await lookupParentInformationAdmin({
        lookupType,
        query,
      });
      setStudents(response.students);
    } catch {
      setStudents([]);
      notifyFailure();
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (item: ParentLookupAdminPackage) => {
    setSelected(item);
    setNewEndDate(item.endDate);
    setRemaining(
      item.remainingSessions === null ? '' : String(item.remainingSessions),
    );
  };

  const save = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const updated = await updateParentLookupPackage(selected.registrationId, {
        newEndDate,
        ...(selected.visitType.toLowerCase() === 'count'
          ? { remainingSessions: Number(remaining) }
          : {}),
      });
      setStudents((current) =>
        current.map((student) => ({
          ...student,
          packages: student.packages.map((item) =>
            item.registrationId === updated.registrationId ? updated : item,
          ),
        })),
      );
      notifyAPIClient('Package updated successfully.');
      setSelected(null);
    } catch {
      notifyFailure();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title="Parent Lookup — Admin"
            subtitle="Review and edit all student package registrations."
          />
          <Card>
            <CardContent>
              <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
                <Select
                  size="small"
                  value={lookupType}
                  onChange={(event) =>
                    setLookupType(event.target.value as ParentLookupType)
                  }
                >
                  <MenuItem value="studentCode">Student code</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                </Select>
                <TextField
                  size="small"
                  label="Lookup"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void lookup();
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => void lookup()}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Lookup'}
                </Button>
              </Box>
              {students.map((student) => (
                <Paper
                  key={student.studentCode}
                  variant="outlined"
                  sx={{ p: 2, mb: 2 }}
                >
                  <Typography variant="h6">
                    {student.displayName} ({student.studentCode})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {student.phone || '—'} · Email:{' '}
                    {student.email || '—'}
                  </Typography>
                  <Stack spacing={1.5} mt={2}>
                    {student.packages.map((item) => (
                      <Paper
                        key={item.registrationId}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                        >
                          <Box>
                            <Typography fontWeight={700}>
                              {item.packageName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.serviceType} · {item.startDate} –{' '}
                              {item.endDate}
                            </Typography>
                          </Box>
                          <Box display="flex" gap={1} alignItems="center">
                            <Chip
                              size="small"
                              label={item.status}
                              color={
                                item.status.toLowerCase() === 'active'
                                  ? 'success'
                                  : 'error'
                              }
                              variant="filled"
                            />
                            <Button
                              size="small"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => openEdit(item)}
                            >
                              Edit
                            </Button>
                          </Box>
                        </Box>
                        <Typography variant="body2">
                          Remaining:{' '}
                          {item.remainingSessions === null
                            ? 'Unlimited'
                            : item.remainingSessions}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              ))}
              {!loading && students.length === 0 && (
                <Typography color="text.secondary">
                  No student found.
                </Typography>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit package</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              type="date"
              label="New end date"
              value={newEndDate}
              onChange={(event) => setNewEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {selected?.visitType.toLowerCase() === 'count' && (
              <TextField
                type="number"
                label="Remaining sessions"
                value={remaining}
                onChange={(event) => setRemaining(event.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={
              saving ||
              !newEndDate ||
              (selected?.visitType.toLowerCase() === 'count' &&
                remaining === '')
            }
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ParentLookupAdmin;
